import React, { useState, useEffect, useMemo } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import './QuizInfoCard.css';
import { Card, Typography, List, Divider, Modal, message } from 'antd';
import { ReactComponent as EditIcon } from '../../assets/icon/edit.svg';
import { ReactComponent as CopyIcon } from '../../assets/icon/copy.svg';
import EditQuizForm from "../EditQuizForm/EditQuizForm";
import BookmarkMenu from "../BookmarkMenu/BookmarkMenu";
import InfoCardFooter, { InfoCardIconButton } from "../InfoCardFooter/InfoCardFooter";
import useTranslation from "../../hooks/useTranslation";
import { useSelector } from 'react-redux';
import quizClient from "../../client/QuizClient";

const { Text } = Typography;
const QuizInfoCard = ({ quiz, onUpdateQuiz }) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // Get selected deck from Redux
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);

    // Determine main and translation languages via useMemo for stability
    const mainLanguage = useMemo(
        () => selectedDeck?.locale ?? { name: 'Original', flag: null },
        [selectedDeck]
    );
    const translationLanguage = useMemo(
        () => selectedDeck?.translateLocale ?? { name: 'Translate', flag: null },
        [selectedDeck]
    );

    useEffect(() => {
        console.log("Selected deck:", selectedDeck);
        console.log("Main language:", mainLanguage);
        console.log("Translation language:", translationLanguage);
    }, [selectedDeck, mainLanguage, translationLanguage]);

    // State for displaying original or translated content
    const [showTranslated, setShowTranslated] = useState(false);
    // State for showing correct answer borders
    const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
    // Modal visibility state
    const [isModalVisible, setIsModalVisible] = useState(false);
    // State for translation mode inside modal
    const [modalTranslationMode, setModalTranslationMode] = useState(false);
    const [isLabelUpdating, setIsLabelUpdating] = useState(false);
    const [localLabel, setLocalLabel] = useState(quiz.label || null);

    useEffect(() => {
        setLocalLabel(quiz.label || null);
    }, [quiz.label]);

    // Toggle question text between original and translation
    const handleQuestionToggle = (e) => {
        e.stopPropagation();
        if (mainLanguage.name !== translationLanguage.name) {
            setShowTranslated((prev) => !prev);
        } else {
            message.info("Switching disabled: same language");
        }
    };

    const handleAnswerClick = (e) => {
        e.stopPropagation();
        setShowCorrectAnswers((prev) => !prev);
    };

    const showModal = () => {
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const handleSave = (updatedQuiz) => {
        if (onUpdateQuiz) {
            onUpdateQuiz(updatedQuiz);
        }
        setIsModalVisible(false);
    };

    const handleDelete = () => {
        message.success('Quiz deleted successfully!');
    };

    const handleLabelChange = async (label) => {
        if (isLabelUpdating) {
            return;
        }

        const previousLabel = localLabel;
        setLocalLabel(label);
        onUpdateQuiz?.({ ...quiz, label });
        setIsLabelUpdating(true);

        try {
            const updatedQuiz = await quizClient.updateQuizLabel(quiz.id, label);
            const persistedLabel = updatedQuiz?.label || null;
            setLocalLabel(persistedLabel);
            onUpdateQuiz?.({ ...quiz, ...updatedQuiz, label: persistedLabel });
        } catch (error) {
            setLocalLabel(previousLabel);
            onUpdateQuiz?.({ ...quiz, label: previousLabel });
            message.error('Failed to update label.');
            console.error('Error updating quiz label:', error);
        } finally {
            setIsLabelUpdating(false);
        }
    };

    // Returns the question text based on the current mode
    const getDisplayedQuestion = () => {
        if (showTranslated) {
            return quiz.translateQuestion && quiz.translateQuestion.trim() !== ""
                ? quiz.translateQuestion
                : "[Translation not available.]";
        }
        return quiz.question;
    };

    // Returns the answer text based on the current mode
    const getDisplayedAnswer = (answer) => {
        if (showTranslated) {
            return answer.translatedText && answer.translatedText.trim() !== ""
                ? answer.translatedText
                : "[Translation not available.]";
        }
        return answer.text;
    };

    // Квиз кладём в буфер как JSON: вопрос вместе с вариантами так удобно
    // вставлять в подсказки для ИИ, ради которых кнопка и появилась.
    const handleCopy = () => {
        const simplifiedQuiz = {
            question: getDisplayedQuestion(),
            answers: quiz.answers.map((answer) => getDisplayedAnswer(answer)),
        };

        navigator.clipboard
            .writeText(JSON.stringify(simplifiedQuiz, null, 2))
            .then(() => {
                message.success(t('common.copied'));
            })
            .catch((err) => {
                message.error(t('common.copyFailed'));
                console.error(err);
            });
    };

    const questionTextColor =
        showTranslated && (!quiz.translateQuestion || quiz.translateQuestion.trim() === "")
            ? "grey"
            : undefined;

    return (
        <>
            <Card
                key={quiz.id}
                className="quizInfoCard"
                bordered={false}
                styles={{ body: { padding: 0 } }}
                style={
                    quiz.answersNotDefinitive
                        ? { borderLeft: '1px solid #800000', borderRight: '1px solid #800000' }
                        : {}
                }
            >
                <div style={{ padding: '16px 16px 0 16px' }}>
                    {/* Clickable question text */}
                    <Text
                        onClick={handleQuestionToggle}
                        style={{
                            whiteSpace: 'pre-line',
                            fontSize: isMobile ? '110%' : '20px',
                            display: 'block',
                            marginBottom: '12px',
                            cursor: mainLanguage.name !== translationLanguage.name ? 'pointer' : 'default',
                            color: questionTextColor,
                        }}
                    >
                        {getDisplayedQuestion()}
                    </Text>

                    {/* List of answers */}
                    <List
                        dataSource={quiz.answers}
                        renderItem={(answer, index) => {
                            const answerTextColor =
                                showTranslated && (!answer.translatedText || answer.translatedText.trim() === "")
                                    ? "grey"
                                    : undefined;
                            return (
                                <List.Item
                                    key={index}
                                    onClick={handleAnswerClick}
                                    style={{
                                        border: showCorrectAnswers && answer.isCorrect ? '1px solid green' : '1px solid transparent',
                                        padding: '8px 16px',
                                        marginBottom: '8px',
                                        transition: 'border 0.3s',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Text
                                        style={{
                                            whiteSpace: 'pre-line',
                                            fontSize: isMobile ? '110%' : '16px',
                                            color: answerTextColor || 'rgb(102,102,102)',
                                        }}
                                    >
                                        {getDisplayedAnswer(answer)}
                                    </Text>
                                </List.Item>
                            );
                        }}
                    />

                    <Divider style={{ margin: '2px 0' }} />

                    {/* Bottom control panel */}
                    <InfoCardFooter>
                        <BookmarkMenu
                            value={localLabel}
                            onChange={handleLabelChange}
                            disabled={isLabelUpdating}
                        />
                        <InfoCardIconButton icon={CopyIcon} title={t('common.copy')} onClick={handleCopy} />
                        <InfoCardIconButton icon={EditIcon} title={t('common.edit')} onClick={showModal} />
                    </InfoCardFooter>
                </div>
            </Card>

            {/* Modal window for editing quiz */}
            <Modal
                title={`Edit Quiz: ${quiz.id}`}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={isMobile ? '90%' : '60%'}
                style={{ top: '10%' }}
            >
                <EditQuizForm
                    quiz={quiz}
                    deckId={quiz.deckId}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                    isTranslationMode={modalTranslationMode}
                    mainLanguage={mainLanguage}
                    translationLanguage={translationLanguage}
                    onToggleLanguage={setModalTranslationMode}
                />
            </Modal>
        </>
    );
};

export default QuizInfoCard;
